import { Box, Button, Stack, Typography, styled, useTheme } from '@mui/material';
import { t } from 'i18next';
import { useMemo, useState } from 'react';

import { IconEye } from '@shared/assets/images/svgs';
import {
  INPUT_PATH_PREFIX,
  TEMPLATE_PLACEHOLDERS,
  TemplatePlaceholder,
  analysisPath,
  deriveInputNames,
  findLegacyPlaceholders,
  TemplateSegment,
  buildTemplateSegments,
  shortenPath,
  wrapPath,
} from '@shared/utils/messageTemplate';

import { BorderedGrid } from '../borderedGrid';
import { CustomTooltip } from '../customTooltip';
import { StyledErrorText } from '../errorText';
import CustomFormLabel from '../forms/theme-elements/CustomFormLabel';
import CustomTextField from '../forms/theme-elements/CustomTextField';
import { InfoDialog } from '../infoDialog/infoDialog';

export type MessageTemplatePreviewValues = {
  agentName?: string;
  campaignName?: string;
  companyName?: string;
};

export type MessageTemplateEditorProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
  previewValues: MessageTemplatePreviewValues;
  placeholders?: readonly TemplatePlaceholder[];
  analysisFields?: string[];
  allowDynamicInput?: boolean;
};

const StyledPlaceholderButton = styled(Button)(({ theme }) => ({
  margin: '4px 0 4px 4px',
  borderRadius: '4px',
  fontSize: '12px',
  textTransform: 'none',
  color: theme.palette.extendedGrey?.dark,
  backgroundColor: theme.palette.background.default,
  '&:hover': {
    color: theme.palette.extendedGrey?.dark,
    backgroundColor: theme.palette.extendedGrey?.neutralLight,
    boxShadow: `0px 1px 3px 0px ${theme.palette.extendedGrey?.neutralLight}`,
  },
}));

const StyledDynamicButton = styled(StyledPlaceholderButton)(({ theme }) => ({
  marginLeft: '12px',
  border: `1px dashed ${theme.palette.extendedGrey?.neutralLight}`,
}));

const IconButton = styled(Box)(({ theme }) => ({
  right: '16px',
  top: '16px',
  position: 'absolute',
  width: '40px',
  height: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.extendedGrey?.lightest,
  borderRadius: '8px',
}));

const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.extendedGrey?.dark,
}));

export const MessageTemplateEditor = ({
  id,
  value,
  onChange,
  label,
  error,
  disabled = false,
  previewValues,
  placeholders = TEMPLATE_PLACEHOLDERS,
  analysisFields = [],
  allowDynamicInput = false,
}: MessageTemplateEditorProps) => {
  const theme = useTheme();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSegments, setPreviewSegments] = useState<TemplateSegment[]>([]);

  const legacyLeftovers = useMemo(() => findLegacyPlaceholders(value), [value]);
  const inputNames = useMemo(() => deriveInputNames(value), [value]);

  const insertAtCursor = (text: string) => {
    const input = document.getElementById(id) as HTMLTextAreaElement | null;
    const cursorPos = input?.selectionStart ?? value.length;

    onChange(value.slice(0, cursorPos) + text + value.slice(cursorPos));

    setTimeout(() => {
      if (!input) {
        return;
      }
      const caret = cursorPos + text.length;
      input.setSelectionRange(caret, caret);
      input.focus();
    }, 0);
  };

  const handleOpenPreview = () => {
    const context = {
      agent: { name: previewValues.agentName || undefined },
      campaign: { name: previewValues.campaignName || undefined },
      company: { name: previewValues.companyName || undefined },
    };

    setPreviewSegments(
      buildTemplateSegments(value, context, { onMissing: (path) => `[${shortenPath(path)}]` }),
    );
    setPreviewOpen(true);
  };

  return (
    <Box>
      <CustomFormLabel sx={{ mt: '20px', fontWeight: 500 }} htmlFor={id}>
        {label ?? t('campaignPage.template')}
      </CustomFormLabel>
      <BorderedGrid
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignContent: 'start',
          alignItems: 'center',
          marginBottom: 0,
          borderBottomWidth: 0,
          padding: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          backgroundColor: theme.palette.extendedGrey?.lightest,
        }}>
        {placeholders.map((placeholder) => (
          <CustomTooltip
            key={placeholder.path}
            title={wrapPath(placeholder.path)}
            placement="top"
            arrow>
            <StyledPlaceholderButton
              onClick={() => insertAtCursor(wrapPath(placeholder.path))}
              color="primary"
              disabled={disabled}
              variant="text">
              {t(`adminMainPage.agent.messageTemplate.placeholders.${placeholder.labelKey}`)}
            </StyledPlaceholderButton>
          </CustomTooltip>
        ))}

        {analysisFields.map((field) => (
          <CustomTooltip key={field} title={wrapPath(analysisPath(field))} placement="top" arrow>
            <StyledPlaceholderButton
              onClick={() => insertAtCursor(wrapPath(analysisPath(field)))}
              color="primary"
              disabled={disabled}
              variant="text">
              {field}
            </StyledPlaceholderButton>
          </CustomTooltip>
        ))}

        {allowDynamicInput ? (
          <CustomTooltip
            title={t('adminMainPage.agent.messageTemplate.dynamicValueHint', {
              example: `{{${INPUT_PATH_PREFIX}name}}`,
            })}
            placement="top"
            arrow>
            <StyledDynamicButton
              onClick={() => insertAtCursor(`{{${INPUT_PATH_PREFIX}`)}
              color="primary"
              disabled={disabled}
              variant="text">
              {t('adminMainPage.agent.messageTemplate.dynamicValue')}
            </StyledDynamicButton>
          </CustomTooltip>
        ) : null}
      </BorderedGrid>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          ...(error
            ? { '& fieldset': { borderColor: `${theme.palette.error.main} !important` } }
            : {}),
        }}>
        <CustomTextField
          sx={{
            marginRight: '10px',
            '& .MuiInputBase-multiline': {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              padding: '0px 50px 0px 0px',
            },
          }}
          value={value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
          disabled={disabled}
          id={id}
          variant="outlined"
          fullWidth
          multiline
          rows={5}
        />
        <IconButton onClick={handleOpenPreview}>
          <IconEye />
        </IconButton>
      </Box>
      {error ? <StyledErrorText>{error}</StyledErrorText> : null}

      <Stack spacing={0.5} sx={{ mt: '8px' }}>
        <HelperText>{t('adminMainPage.agent.messageTemplate.helper')}</HelperText>
        {allowDynamicInput && inputNames.length > 0 ? (
          <HelperText>
            {t('adminMainPage.agent.messageTemplate.agentWillProvide', {
              names: inputNames.join(', '),
            })}
          </HelperText>
        ) : null}
        {legacyLeftovers.length > 0 ? (
          <StyledErrorText>
            {t('adminMainPage.agent.messageTemplate.legacyLeftover', {
              placeholders: legacyLeftovers.join(', '),
            })}
          </StyledErrorText>
        ) : null}
      </Stack>

      <InfoDialog
        showDialogFlag={previewOpen}
        handleClose={() => setPreviewOpen(false)}
        dialogTitle={t('campaignPage.preview')}
        dialogContentText={
          <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>
            {previewSegments.map((segment, index) => {
              if (segment.kind === 'text') {
                return <span key={index}>{segment.text}</span>;
              }

              return (
                <Box
                  component="span"
                  key={index}
                  sx={{
                    fontWeight: 600,
                    color:
                      segment.kind === 'value'
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                  }}>
                  {segment.text}
                </Box>
              );
            })}
          </Box>
        }
        confirmDialog={() => setPreviewOpen(false)}
      />
    </Box>
  );
};
